/**
 * Hub 推荐计划区块（hub-referral-part）
 *
 * 对应 Liquid：`sections/hub-referral-part.liquid`
 * 样式：`assets/section-hub-referral-part.css`
 *
 * ## 职责
 * - 已入会会员的推荐链接展示与复制
 * - 推荐进度（大数字、分段进度条、统计卡片）
 * - 奖励产品列表的解锁/锁定状态
 *
 * ## 挂载方式
 * 自定义元素 `<hub-referral-part>` 在 connected 时读取子节点 `[data-json]` 配置，
 * 并在 `[data-vue-render]` 上挂载 Vue 3 应用（分隔符 `${ }`，与 Liquid 默认 `{{ }}` 区分）。
 *
 * ## 接口依赖（@theme/shokz-member）
 * | 方法 | 说明 |
 * |------|------|
 * | `getMemberDataByOnceCall()` | 获取会员信息，提供 `ShokzMember.id` 作为 referral_id |
 * | `getReferralLink()` | POST 获取推荐链接 |
 * | `getReferralProgress(referralId)` | POST `/api/v1/us-member/get-progress`，获取挑战进度列表 |
 * | `getMemberCoupons()` | POST `/api/v1/us-member/get-unused-coupon`，获取未使用优惠券列表 |
 *
 * ## 进度数据解析（get-progress）
 * 响应结构：`{ code, data: { data: Challenge[] } }`
 *
 * 从挑战列表中选取 Referral 挑战（多级回退）：
 * 1. `cta_label` 含 "referr your friends"
 * 2. `title` 含 "referral free prize"
 * 3. 首个 `progress.criteria` 含 `action === "successful_referral"` 的项
 *
 * 再取其 `progress.criteria` 中 **action === "successful_referral"** 的条件（若无则取第一项）：
 * - `criteria.threshold` → 每周期所需人数（通常为 7）
 * - `completions` → 已完成整轮次数（递增表示该轮已完成且券已发放，进度重置为 0/7）
 *
 * ## 累计有效推荐（member-info）
 * `lifetimeReferrals` 取自 `member-info` 返回的 `properties.referral_valid_count`。
 *
 * ## 前端字段映射
 * | Vue 字段 | 含义 |
 * |----------|------|
 * | `lifetimeReferrals` | `member-info` → `properties.referral_valid_count` |
 * | `pendingReferrals` | `member-info` → `properties.pending_referral` |
 * | `availableRedemptions` | `get-unused-coupon` 中 `shopify_coupon_code` 以 `US-FR-PD` 开头且未过期的张数 |
 * | `cycleSize` | API `threshold`，进度分母（如 7） |
 * | `challengeCompletions` | API `completions`；未加载时为 null |
 * | `cycleProgress` | `lifetime - threshold × completions`（clamp 0~threshold）；completions 未加载时回退取模 |
 * | `pendingBarCount` | 进度条上展示的 pending 格数 = `min(pendingReferrals, cycleSize - cycleProgress)` |
 * | `rewardsUnlocked` | `availableRedemptions > 0`（持有可兑换的推荐奖励券，与进度解耦） |
 *
 * ## 周期进度算法
 * `cycleProgress = clamp(lifetime - threshold × completions, 0, threshold)`。
 * 例：lifetime = 7、completions = 1 → 0/7（新一轮）；lifetime = 10、completions = 1 → 3/7。
 * `get-progress` 未返回时回退：`lifetime % threshold`（余数为 0 且 lifetime > 0 时显示 threshold）。
 *
 * ## 可兑换次数（get-unused-coupon）
 * 调用 `getMemberCoupons()`，在返回的 `data` 数组中统计
 * `shopify_coupon_code` 以 **`US-FR-PD`** 开头、且 **`expires_at` 未过期** 的未使用券数量，写入 `availableRedemptions`。
 * 点击 Redeem 时使用其中 **`expires_at` 最近（最早到期）** 的一张未过期券。
 * 奖励区副标题：`availableRedemptions > 0` 用 `rewards_subtitle`（含 `${count}`）；
 * 为 0 时用 `rewards_subtitle_locked`（默认 *Unlock when you reach 7 valid referrals.*）。
 *
 * ## 复制推荐链接
 * - 成功：Toast 提示（时长 4s），按钮置灰并改文案，Toast 消失后再恢复（+300ms 过渡）
 * - 失败：Toast 错误提示
 *
 * ## Redeem GA4 调试（无兑换额度时测 select_content）
 * URL 加 `?referral_redeem_debug=1`：解锁 Redeem 按钮，点击仅推送 GA4，不真实兑换。
 * 控制台查看：`window.dataLayer.slice(-1)`
 *
 * ## Section JSON 配置（data-json）
 * 由 Liquid 注入，常用键：`referral_cycle_size`、`rewards_subtitle`、`locked_button_label`、
 * `copy_toast_message` 等（接口未返回前 `cycleSize` 使用 `referral_cycle_size` 兜底）。
 *
 * @module section-hub-referral-part
 */

import ShokzMember from '@theme/shokz-member';
import { createApp, ref, computed, onMounted } from 'vue';
/**
 * @import {SimpleToast} from './component-simple-toast'
 */

/** @type {SimpleToast} */
const simpleToast = document.getElementById('simple-toast');

const DEFAULT_CYCLE_SIZE = 7;
const COPY_TOAST_DURATION = 4000;
const COPY_TOAST_HIDE_TRANSITION = 300;
const REFERRAL_CTA_MATCH = 'referr your friends';
const REFERRAL_TITLE_MATCH = 'referral free prize';
/** 推荐奖励可兑换券 code 前缀（get-unused-coupon，如 US-FR-PD15-…） */
const REFERRAL_REDEMPTION_COUPON_PREFIX = 'US-FR-PD';
const REWARDS_CAROUSEL_MAX_WIDTH = 999;
const CART_REQUEST_INIT = { credentials: 'same-origin', cache: 'no-store' };

const HUB_REFERRAL_GA4_CONTENT_TYPE = 'section';
const HUB_REFERRAL_GA4_CONTENT_NAME = 'hub_referral_section';

function pushSelectContentEvent(
  buttonName,
  options = {}
) {
  const contentType = options.contentType || HUB_REFERRAL_GA4_CONTENT_TYPE;
  const contentName = options.contentName || HUB_REFERRAL_GA4_CONTENT_NAME;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event_parameters: null });
  window.dataLayer.push({
    event: 'ga4Event',
    event_name: 'select_content',
    event_parameters: {
      content_type: contentType,
      content_name: contentName,
      button_name: buttonName,
    },
  });
}

const HUB_REFERRAL_GA4_REDEEM_CONTENT_TYPE = 'Color';
const HUB_REFERRAL_GA4_REDEEM_CONTENT_NAME = 'Color_Referral';

/**
 * @param {string} sku
 * @returns {{ title?: string, name?: string, size?: string, color?: string, type?: string }}
 */
function getProductGa4Info(sku) {
  if (!sku || typeof getProductInfo !== 'function') {
    return {};
  }
  const info = getProductInfo(sku);
  if (!info || info === 'Not found' || typeof info !== 'object') {
    return {};
  }
  return info;
}

/**
 * @param {HTMLButtonElement} redeemBtn
 * @param {HTMLElement} source
 */
function syncRewardRedeemButtonProductData(redeemBtn, source) {
  if (!(redeemBtn instanceof HTMLButtonElement) || !source?.dataset?.productData) return;
  redeemBtn.dataset.productData = source.dataset.productData;
}

/**
 * @param {string[]} productItem
 * @param {string | undefined} colorOptionPosition
 * @returns {string}
 */
function getRewardColorFromProductItem(productItem, colorOptionPosition) {
  const position = Number(colorOptionPosition);
  if (!position || !Array.isArray(productItem)) return '';

  const indexByPosition = { 1: 6, 2: 7 };
  const index = indexByPosition[position];
  if (index == null) return '';

  const value = String(productItem[index] || '').trim();
  if (!value || value === 'No' || value === 'Default Title') return '';
  return value;
}

/**
 * @param {string[]} productItem
 * @param {{ title?: string, name?: string, color?: string }} productInfo
 * @param {HTMLElement | null | undefined} card
 * @returns {string}
 */
function buildRedeemButtonName(productItem, productInfo, card) {
  const productName = (
    card?.querySelector('.hub-referral-part__reward-name')?.textContent?.trim() ||
    String(productItem[8] || '').trim()
  );

  const selectedSwatch = card?.querySelector('.hub-referral-part__reward-swatch.is-selected');
  const swatchColor = selectedSwatch?.getAttribute('aria-label')?.trim() || '';
  const optionColor = getRewardColorFromProductItem(
    productItem,
    card?.dataset?.colorOptionPosition
  );
  const colorName = (swatchColor || String(productInfo.color || '').trim() || optionColor).trim();

  if (!productName) return colorName;
  if (!colorName) return productName;

  const productLower = productName.toLowerCase();
  const colorLower = colorName.toLowerCase();

  if (productLower === colorLower) return productName;
  if (colorLower.startsWith(`${productLower} `)) return colorName;
  if (productLower.endsWith(` ${colorLower}`)) return productName;

  return `${productName} ${colorName}`;
}

/**
 * @param {HTMLButtonElement} button
 */
function pushRedeemSelectContentEvent(button) {
  if (!(button instanceof HTMLButtonElement)) return;

  const card = button.closest('.hub-referral-part__reward-card');
  const productDataRaw =
    button.dataset.productData ||
    card?.querySelector('.product-data-div')?.getAttribute('data-product-data');

  if (!productDataRaw) return;

  const productItem = productDataRaw.split('::');
  const sku = productItem[0] || '';
  const productInfo = getProductGa4Info(sku);
  const buttonName = buildRedeemButtonName(productItem, productInfo, card);
  if (!buttonName) return;

  pushSelectContentEvent(buttonName, {
    contentType: HUB_REFERRAL_GA4_REDEEM_CONTENT_TYPE,
    contentName: HUB_REFERRAL_GA4_REDEEM_CONTENT_NAME,
  });
}

/**
 * Referral debug 配置（任选其一启用 debug）：
 * 1. `REFERRAL_DEBUG_CONFIG.enabled` 设为 true
 * 2. URL 参数 `?referral_debug=1`
 *
 * Pending 测试（无需开启完整 debug）：
 * - URL 参数 `?referral_pending=1`（可改为任意非负整数）
 * - 或在 `referral_debug=1` 时使用 `REFERRAL_DEBUG_CONFIG.pending_referrals`
 *
 * @typedef {{ code?: string, shopify_coupon_code?: string, expires_at?: string }} ReferralCouponLike
 */
const REFERRAL_DEBUG_CONFIG = {
  enabled: false,
  available_redemptions: 1,
  pending_referrals: 1,
  coupons: [
    {
      shopify_coupon_code: 'US-FR-PD15-DEBUG001',
      expires_at: '2026-10-03T23:59:59-05:00',
    },
    {
      shopify_coupon_code: 'US-FR-PD15-DEBUG002',
      expires_at: '2026-08-03T23:59:59-05:00',
    },
    {
      shopify_coupon_code: 'US-FR-PD20-DEBUG003',
      expires_at: '2026-09-03T23:59:59-05:00',
    },
  ],
};

/** @returns {boolean} */
function isReferralDebugEnabled() {
  if (new URLSearchParams(window.location.search).get('referral_debug') === '1') {
    return true;
  }
  return Boolean(REFERRAL_DEBUG_CONFIG.enabled);
}

/** @returns {boolean} */
function isReferralRedeemGa4DebugEnabled() {
  return new URLSearchParams(window.location.search).get('referral_redeem_debug') === '1';
}

/**
 * @param {import('vue').Ref<number>} availableRedemptionsRef
 * @param {import('vue').Ref<ReferralCouponLike[]>} referralCouponsRef
 */
function applyRedeemGa4DebugOverrides(availableRedemptionsRef, referralCouponsRef) {
  if (!isReferralRedeemGa4DebugEnabled()) return;

  const debugCoupons = filterAvailableReferralRedemptionCoupons(REFERRAL_DEBUG_CONFIG.coupons);
  if (!referralCouponsRef.value.length) {
    referralCouponsRef.value = debugCoupons;
  }
  availableRedemptionsRef.value = Math.max(availableRedemptionsRef.value, 1);

  console.info(
    '[hub-referral-part]: referral_redeem_debug=1 — Redeem 已解锁；点击仅推送 GA4，不会真实兑换。查看: window.dataLayer.slice(-1)'
  );
}

/**
 * 返回 pending 调试值；未启用时返回 null，沿用接口数据。
 * @returns {number | null}
 */
function getPendingDebugValue() {
  const params = new URLSearchParams(window.location.search);
  const param = params.get('referral_pending');
  if (param !== null && param !== '') {
    const count = Number(param);
    return Number.isFinite(count) && count >= 0 ? count : null;
  }

  if (!isReferralDebugEnabled()) {
    return null;
  }

  const configured = Number(REFERRAL_DEBUG_CONFIG.pending_referrals);
  return Number.isFinite(configured) && configured >= 0 ? configured : null;
}

/**
 * @param {import('vue').Ref<number>} pendingRef
 */
function applyPendingDebugOverride(pendingRef) {
  const debugPending = getPendingDebugValue();
  if (debugPending === null) return;

  pendingRef.value = debugPending;
  console.info('[hub-referral-part]: pending debug override', debugPending);
}

/**
 * @param {ReferralCouponLike} coupon
 * @returns {{ code: string, expires_at: string } | null}
 */
function normalizeReferralCoupon(coupon) {
  const code = String(coupon?.code || coupon?.shopify_coupon_code || '').trim();
  const expiresAt = String(coupon?.expires_at || '').trim();
  if (!code || !expiresAt) return null;
  return { code, expires_at: expiresAt };
}

/**
 * 选取未过期且过期时间最近（最早到期）的折扣码。
 * @param {ReferralCouponLike[]} coupons
 * @returns {{ code: string, expires_at: string } | null}
 */
function pickNearestExpiryCoupon(coupons) {
  const now = Date.now();
  return (coupons || [])
    .map(normalizeReferralCoupon)
    .filter((coupon) => coupon && new Date(coupon.expires_at).getTime() > now)
    .sort(
      (a, b) =>
        new Date(a.expires_at).getTime() - new Date(b.expires_at).getTime()
    )[0] || null;
}

async function getCart() {
  const response = await fetch('/cart.js', {
    ...CART_REQUEST_INIT,
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  });
  if (!response.ok) {
    throw new Error('Failed to load cart. Please try again.');
  }
  return response.json();
}

function cartHasItems(cart) {
  if (!cart) return false;
  if (Number(cart.item_count) > 0) return true;
  return Array.isArray(cart.items) && cart.items.length > 0;
}

async function clearCart() {
  const response = await fetch('/cart/clear.js', {
    method: 'POST',
    ...CART_REQUEST_INIT,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  });
  if (!response.ok) {
    throw new Error('Failed to clear cart. Please try again.');
  }
  return response.json();
}

/**
 * @param {number | string} variantId
 */
async function addVariantToCart(variantId) {
  const response = await fetch(window.routes.cart_add_url, {
    method: 'POST',
    ...CART_REQUEST_INIT,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: JSON.stringify({
      items: [{ id: Number(variantId), quantity: 1 }],
    }),
  });

  let data = null;
  try {
    data = await response.json();
  } catch (error) {
    // ignore parse error
  }

  if (!response.ok) {
    throw new Error(data?.message || 'Failed to add reward to cart. Please try again.');
  }
  return data;
}

/**
 * @param {string} discountCode
 */
async function applyCartDiscount(discountCode) {
  const response = await fetch('/cart/update.js', {
    method: 'POST',
    ...CART_REQUEST_INIT,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: JSON.stringify({ discount: discountCode }),
  });

  let data = null;
  try {
    data = await response.json();
  } catch (error) {
    // ignore parse error
  }

  if (!response.ok || data?.status === 422) {
    throw new Error(data?.description || data?.message || 'Failed to apply discount code.');
  }

  const applied = (data?.discount_codes || []).some(
    (item) =>
      String(item?.code || '').toUpperCase() === discountCode.toUpperCase() &&
      item?.applicable !== false
  );
  if (!applied) {
    throw new Error('Discount code could not be applied.');
  }

  return data;
}

/**
 * @param {number | string} variantId
 * @param {string} discountCode
 */
async function redeemReferralReward(variantId, discountCode) {
  const cart = await getCart();
  if (cartHasItems(cart)) {
    simpleToast?.show('Your current cart will be cleared to redeem this reward.');
  }
  await clearCart();
  await addVariantToCart(variantId);
  await applyCartDiscount(discountCode);
  window.location.href = '/checkout';
}

/**
 * 移动端轮播：统一各卡片产品标题区域高度（按最高标题对齐）。
 * @param {HTMLElement | null} root
 */
function syncRewardCardTitleHeights(root) {
  const track = root?.querySelector('[data-rewards-track]');
  if (!track) return;

  const titles = Array.from(
    track.querySelectorAll('.hub-referral-part__reward-name')
  );
  if (!titles.length) return;

  const isCarouselMode = window.matchMedia(
    `(max-width: ${REWARDS_CAROUSEL_MAX_WIDTH}px)`
  ).matches;

  titles.forEach((title) => {
    title.style.minHeight = '';
  });

  if (!isCarouselMode) return;

  const measureAndApply = () => {
    const maxHeight = titles.reduce((max, title) => {
      return Math.max(max, title.offsetHeight);
    }, 0);

    if (maxHeight > 0) {
      titles.forEach((title) => {
        title.style.minHeight = `${maxHeight}px`;
      });
    }
  };

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(measureAndApply);
  });

  if (document.fonts?.ready) {
    document.fonts.ready.then(measureAndApply).catch(() => {});
  }
}

/**
 * <1000px 奖励区横向轮播 + 底部分页点；≥1000px 保持 CSS 网格并隐藏分页。
 * 分页点按「屏」计算：页数 = 卡片数 − 一屏可见数 + 1（例如 3 张卡、一屏 2 张 → 2 个点）。
 * @param {HTMLElement | null} root
 */
function initRewardsCarousel(root) {
  const track = root?.querySelector('[data-rewards-track]');
  const pagination = root?.querySelector('[data-rewards-pagination]');
  if (!track || !pagination) return;

  const mediaQuery = window.matchMedia(`(max-width: ${REWARDS_CAROUSEL_MAX_WIDTH}px)`);

  /** @type {(() => void) | undefined} */
  let onScroll = undefined;
  /** @type {ResizeObserver | undefined} */
  let resizeObserver = undefined;

  const getSlides = () =>
    Array.from(track.querySelectorAll('.hub-referral-part__reward-card'));

  /**
   * @returns {{ slideCount: number, visibleCount: number, pageCount: number, step: number }}
   */
  const getCarouselMetrics = () => {
    const slideEls = getSlides();
    const slideCount = slideEls.length;
    if (!slideCount) {
      return { slideCount: 0, visibleCount: 1, pageCount: 0, step: 0 };
    }

    const slideWidth = slideEls[0].offsetWidth;
    const trackStyles = getComputedStyle(track);
    const gap = parseFloat(trackStyles.columnGap || trackStyles.gap) || 16;
    const step = slideWidth + gap;
    const visibleCount = Math.max(
      1,
      Math.floor((track.clientWidth + gap) / step)
    );
    const pageCount = Math.max(1, slideCount - visibleCount + 1);

    return { slideCount, visibleCount, pageCount, step };
  };

  const scrollToPage = (pageIndex) => {
    const slideEls = getSlides();
    const { slideCount, visibleCount } = getCarouselMetrics();
    const maxSlideIndex = Math.max(0, slideCount - visibleCount);
    const slideIndex = Math.min(pageIndex, maxSlideIndex);
    const slide = slideEls[slideIndex];
    if (!slide) return;
    track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
  };

  const updateActiveDot = () => {
    const dots = pagination.querySelectorAll('.hub-referral-part__rewards-dot');
    const { pageCount, step } = getCarouselMetrics();
    if (!dots.length || !pageCount || !step) return;

    const activePage = Math.min(
      Math.max(0, Math.round(track.scrollLeft / step)),
      pageCount - 1
    );

    dots.forEach((dot, index) => {
      dot.classList.toggle('is-active', index === activePage);
      dot.setAttribute('aria-current', index === activePage ? 'true' : 'false');
    });
  };

  const buildPagination = () => {
    pagination.innerHTML = '';
    const { pageCount } = getCarouselMetrics();
    if (pageCount <= 1) return;

    for (let pageIndex = 0; pageIndex < pageCount; pageIndex += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = `hub-referral-part__rewards-dot${pageIndex === 0 ? ' is-active' : ''}`;
      dot.dataset.pageIndex = String(pageIndex);
      dot.setAttribute('aria-label', `Page ${pageIndex + 1} of ${pageCount}`);
      dot.setAttribute('aria-current', pageIndex === 0 ? 'true' : 'false');
      dot.addEventListener('click', () => scrollToPage(pageIndex));
      pagination.appendChild(dot);
    }
  };

  const refreshCarousel = () => {
    buildPagination();
    const { pageCount } = getCarouselMetrics();
    pagination.hidden = pageCount <= 1;
    updateActiveDot();
  };

  const teardownCarousel = () => {
    if (onScroll) {
      track.removeEventListener('scroll', onScroll);
      onScroll = undefined;
    }
    resizeObserver?.disconnect();
    resizeObserver = undefined;
    pagination.innerHTML = '';
    pagination.hidden = true;
    track.scrollLeft = 0;
    syncRewardCardTitleHeights(root);
  };

  const setupCarousel = () => {
    syncRewardCardTitleHeights(root);
    refreshCarousel();

    if (!pagination.hidden && !onScroll) {
      onScroll = () => window.requestAnimationFrame(updateActiveDot);
      track.addEventListener('scroll', onScroll, { passive: true });
    }

    resizeObserver?.disconnect();
    resizeObserver = new ResizeObserver(() => {
      syncRewardCardTitleHeights(root);
      refreshCarousel();
    });
    resizeObserver.observe(track);
  };

  const applyMode = () => {
    if (mediaQuery.matches) {
      setupCarousel();
    } else {
      teardownCarousel();
    }
  };

  mediaQuery.addEventListener('change', applyMode);
  applyMode();

  return () => {
    mediaQuery.removeEventListener('change', applyMode);
    teardownCarousel();
  };
}

/**
 * Progress within the current referral cycle using completions when available.
 * @param {number} lifetime
 * @param {number} threshold
 * @param {number | null | undefined} completions Completed full cycles from get-progress
 * @returns {number}
 */
function getReferralCycleProgress(lifetime, threshold = DEFAULT_CYCLE_SIZE, completions) {
  const size = Math.max(1, threshold);
  const safeLifetime = Math.max(0, lifetime);

  if (completions != null && Number.isFinite(completions) && completions >= 0) {
    const progress = safeLifetime - size * completions;
    return Math.max(0, Math.min(size, progress));
  }

  const remainder = safeLifetime % size;
  return remainder === 0 && safeLifetime > 0 ? size : remainder;
}

/**
 * @param {unknown[]} challenges
 * @returns {any | null}
 */
function findReferralChallenge(challenges) {
  if (!Array.isArray(challenges) || challenges.length === 0) return null;

  const byCtaLabel = challenges.find((item) =>
    String(item?.cta_label || '')
      .trim()
      .toLowerCase()
      .includes(REFERRAL_CTA_MATCH)
  );
  if (byCtaLabel) return byCtaLabel;

  const byTitle = challenges.find((item) =>
    String(item?.title || '')
      .trim()
      .toLowerCase()
      .includes(REFERRAL_TITLE_MATCH)
  );
  if (byTitle) return byTitle;

  return (
    challenges.find((item) =>
      item?.progress?.criteria?.some((c) => c?.action === 'successful_referral')
    ) || null
  );
}

/**
 * @param {any} payload
 * @returns {string}
 */
function extractReferralLink(payload) {
  const data = payload?.data;
  if (typeof data === 'string' && data) return data;
  if (typeof data?.referral_link === 'string') return data.referral_link;
  if (typeof data?.link === 'string') return data.link;
  if (typeof payload?.referral_link === 'string') return payload.referral_link;
  return '';
}

/**
 * @param {any} payload
 * @returns {{ current: number, threshold: number, isCompleted: boolean, completions: number } | null}
 */
function extractReferralChallengeProgress(payload) {
  const challenges = payload?.data?.data;
  if (!Array.isArray(challenges)) return null;

  const challenge = findReferralChallenge(challenges);
  if (!challenge) return null;

  const criteria =
    challenge.progress?.criteria?.find((c) => c?.action === 'successful_referral') ||
    challenge.progress?.criteria?.[0];
  if (!criteria) return null;

  const threshold = Math.max(1, Number(criteria.threshold) || DEFAULT_CYCLE_SIZE);
  const current = Math.max(0, Number(criteria.current) || 0);
  const completions = Math.max(0, Number(challenge.completions) || 0);

  return {
    current,
    threshold,
    isCompleted: Boolean(criteria.is_completed),
    completions,
  };
}

/**
 * @param {string | null | undefined} code
 * @returns {boolean}
 */
function isReferralRedemptionCouponCode(code) {
  const normalized = String(code || '').trim();
  return (
    normalized.length > 0 &&
    normalized.startsWith(REFERRAL_REDEMPTION_COUPON_PREFIX)
  );
}

/**
 * 筛选 Referral 可兑换折扣码（shopify_coupon_code 以 US-FR-PD 开头）。
 * @param {import('./shokz-member-type').Coupon[] | null | undefined} coupons
 * @returns {import('./shokz-member-type').Coupon[]}
 */
function filterReferralRedemptionCoupons(coupons) {
  if (!Array.isArray(coupons)) return [];
  return coupons.filter((coupon) =>
    isReferralRedemptionCouponCode(coupon?.shopify_coupon_code)
  );
}

/**
 * @param {ReferralCouponLike} coupon
 * @returns {boolean}
 */
function isReferralCouponNotExpired(coupon) {
  const expiresAt = String(coupon?.expires_at || '').trim();
  if (!expiresAt) return false;
  return new Date(expiresAt).getTime() > Date.now();
}

/**
 * 筛选未过期的 Referral 可兑换折扣码（前缀 US-FR-PD + expires_at 有效）。
 * @param {import('./shokz-member-type').Coupon[] | null | undefined} coupons
 * @returns {import('./shokz-member-type').Coupon[]}
 */
function filterAvailableReferralRedemptionCoupons(coupons) {
  return filterReferralRedemptionCoupons(coupons).filter(isReferralCouponNotExpired);
}

/**
 * 统计推荐奖励可用兑换次数（未使用券中 code 以 US-FR-PD 开头且未过期）。
 * @param {import('./shokz-member-type').Coupon[] | null | undefined} coupons
 * @returns {number}
 */
function countReferralAvailableRedemptions(coupons) {
  return filterAvailableReferralRedemptionCoupons(coupons).length;
}

/**
 * @param {import('./shokz-member-type').MemberData | null | undefined} memberData
 * @returns {number}
 */
function extractLifetimeReferrals(memberData) {
  const raw = memberData?.properties?.referral_valid_count;
  const count = Number(raw);
  return Number.isFinite(count) && count >= 0 ? count : 0;
}

/**
 * @param {import('./shokz-member-type').MemberData | null | undefined} memberData
 * @returns {number}
 */
function extractPendingReferrals(memberData) {
  const raw = memberData?.properties?.pending_referral;
  const count = Number(raw);
  return Number.isFinite(count) && count >= 0 ? count : 0;
}

/**
 * @param {HTMLButtonElement | null | undefined} button
 * @returns {boolean}
 */
function isRewardVariantAvailable(button) {
  return (
    Boolean(button?.dataset?.variantId) &&
    button?.dataset?.variantAvailable === 'true'
  );
}

/**
 * @param {HTMLButtonElement | null | undefined} button
 * @param {boolean} isRedeeming
 */
function syncRewardRedeemButton(button, isRedeeming) {
  if (!button) return;
  button.disabled = isRedeeming || !isRewardVariantAvailable(button);
}

/**
 * @param {HTMLElement | null | undefined} root
 * @param {boolean} isRedeeming
 */
function syncAllRewardRedeemButtons(root, isRedeeming) {
  root?.querySelectorAll('.hub-referral-part__reward-btn--active').forEach((btn) => {
    if (btn instanceof HTMLButtonElement) {
      syncRewardRedeemButton(btn, isRedeeming);
    }
  });
}

/**
 * @typedef {{ url?: string, width?: number, height?: number, alt?: string }} RewardVariantImage
 */

/** @type {WeakMap<HTMLElement, Record<string, RewardVariantImage>>} */
const rewardVariantImageMapCache = new WeakMap();

/**
 * @param {HTMLElement | null | undefined} card
 * @returns {Record<string, RewardVariantImage> | null}
 */
function getRewardVariantImageMap(card) {
  if (!card) return null;

  const cached = rewardVariantImageMapCache.get(card);
  if (cached) return cached;

  const mapEl = card.querySelector('[data-reward-variant-images]');
  if (!(mapEl instanceof HTMLScriptElement)) return null;

  try {
    const parsed = JSON.parse(mapEl.textContent || '{}');
    if (!parsed || typeof parsed !== 'object') return null;
    rewardVariantImageMapCache.set(card, parsed);
    return parsed;
  } catch {
    return null;
  }
}

/**
 * @param {HTMLElement | null | undefined} card
 * @param {string | undefined} variantId
 * @returns {RewardVariantImage | null}
 */
function getRewardVariantImage(card, variantId) {
  if (!variantId) return null;
  const map = getRewardVariantImageMap(card);
  const image = map?.[String(variantId)];
  return image?.url ? image : null;
}

/**
 * 切换奖励卡片主图为当前色块对应 variant 图。
 * @param {HTMLElement | null | undefined} card
 * @param {HTMLButtonElement} swatch
 */
function updateRewardCardImage(card, swatch) {
  const img = card?.querySelector('[data-reward-image]');
  if (!(img instanceof HTMLImageElement)) return;

  const variantImage =
    (swatch.dataset.variantImage
      ? {
          url: swatch.dataset.variantImage,
          width: swatch.dataset.variantImageWidth
            ? Number(swatch.dataset.variantImageWidth)
            : undefined,
          height: swatch.dataset.variantImageHeight
            ? Number(swatch.dataset.variantImageHeight)
            : undefined,
          alt: swatch.dataset.variantImageAlt,
        }
      : null) || getRewardVariantImage(card, swatch.dataset.variantId);

  if (!variantImage?.url) return;

  img.src = variantImage.url;
  if (variantImage.width) {
    img.width = variantImage.width;
  }
  if (variantImage.height) {
    img.height = variantImage.height;
  }
  if (variantImage.alt) {
    img.alt = variantImage.alt;
  }
}

/**
 * 奖励色块切换：更新选中态，并同步兑换按钮的 variant 与库存状态。
 * @param {HTMLElement | null} root
 */
function initRewardSwatchSelection(root) {
  if (!root) return;
  root.addEventListener('click', (event) => {
    const swatch = event.target.closest('.hub-referral-part__reward-swatch');
    if (!swatch || !(swatch instanceof HTMLButtonElement) || swatch.disabled) return;
    event.preventDefault();
    event.stopPropagation();

    const group = swatch.closest('.hub-referral-part__reward-swatches');
    group?.querySelectorAll('.hub-referral-part__reward-swatch').forEach((btn) => {
      btn.classList.remove('is-selected');
      btn.setAttribute('aria-pressed', 'false');
    });
    swatch.classList.add('is-selected');
    swatch.setAttribute('aria-pressed', 'true');

    const card = swatch.closest('.hub-referral-part__reward-card');
    updateRewardCardImage(card, swatch);
    const redeemBtn = card?.querySelector('.hub-referral-part__reward-btn--active');
    const variantId = swatch.dataset.variantId;
    if (redeemBtn instanceof HTMLButtonElement && variantId) {
      redeemBtn.dataset.variantId = variantId;
      redeemBtn.dataset.variantAvailable = swatch.dataset.variantAvailable || 'false';
      syncRewardRedeemButtonProductData(redeemBtn, swatch);
      syncRewardRedeemButton(redeemBtn, false);
    }
  });
}

/**
 * @param {HubReferralPart} section
 */
function createHubReferralPartVueEntry(section) {
  const sectionData = section.data || {};
  const fallbackCycleSize = Number(sectionData.referral_cycle_size) || DEFAULT_CYCLE_SIZE;
  const rewardsSubtitleTemplate =
    sectionData.rewards_subtitle ||
    'You have ${count} available redemption. Choose your reward and redeem it anytime.';
  const rewardsSubtitleLocked =
    sectionData.rewards_subtitle_locked ||
    'Unlock when you reach ${cycleSize} valid referrals.';

  const mountRoot = section.querySelector('[data-vue-render]');

  const app = createApp({
    delimiters: ['${', '}'],
    setup() {
      const loading = ref(true);
      const redeemLoading = ref(false);
      const referralLink = ref('');
      const linkCopied = ref(false);
      const cycleSize = ref(fallbackCycleSize);
      const lifetimeReferrals = ref(0);
      const pendingReferrals = ref(0);
      /** @type {import('vue').Ref<number | null>} null = get-progress 未返回，回退取模 */
      const challengeCompletions = ref(null);
      const availableRedemptions = ref(0);
      /** @type {import('vue').Ref<ReferralCouponLike[]>} */
      const referralCoupons = ref([]);
      const debugMode = ref(false);

      const cycleProgress = computed(() =>
        getReferralCycleProgress(
          lifetimeReferrals.value,
          cycleSize.value,
          challengeCompletions.value
        )
      );
      const pendingBarCount = computed(() => {
        const remaining = Math.max(0, cycleSize.value - cycleProgress.value);
        return Math.min(pendingReferrals.value, remaining);
      });
      const lockedButtonLabel = ref(
        sectionData.locked_button_label || 'Available after 7 valid referrals'
      );
      const copyToastMessage = ref(
        sectionData.copy_toast_message ||
          'Referral link copied. Share it with your friends.'
      );

      const referralLinkDisplay = computed(() => {
        if (referralLink.value) return referralLink.value;
        return loading.value ? 'Loading referral link…' : 'Unable to load referral link';
      });

      const cycleSteps = computed(() =>
        Array.from({ length: cycleSize.value }, (_, index) => index + 1)
      );

      const cycleTicks = computed(() =>
        Array.from({ length: cycleSize.value + 1 }, (_, index) => index)
      );

      const rewardsUnlocked = computed(() => availableRedemptions.value > 0);

      const rewardsSubtitleHtml = computed(() => {
        const count = availableRedemptions.value;
        if (count <= 0) {
          return rewardsSubtitleLocked.replace(
            /\$\{cycleSize\}/g,
            String(cycleSize.value)
          );
        }
        const plural = count === 1 ? '' : 's';
        return rewardsSubtitleTemplate
          .replace(/\$\{count\}/g, String(count))
          .replace(/\$\{plural\}/g, plural);
      });

      const fetchReferralLink = () => {
        return ShokzMember.getReferralLink()
          .then((res) => {
            if (res?.code === 0) {
              const link = extractReferralLink(res);
              if (link) {
                referralLink.value = link;
                return;
              }
            }
            console.warn('[hub-referral-part]: getReferralLink failed', res);
          })
          .catch((error) => {
            console.error('[hub-referral-part]: getReferralLink error', error);
          });
      };

      const applyReferralProgressState = (lifetime, pending, progressRes) => {
        lifetimeReferrals.value = lifetime;
        pendingReferrals.value = pending;
        applyPendingDebugOverride(pendingReferrals);

        if (progressRes == null) {
          challengeCompletions.value = null;
          return;
        }
        if (progressRes?.code !== 0) {
          console.warn('[hub-referral-part]: getReferralProgress failed', progressRes);
          challengeCompletions.value = null;
          return;
        }
        const progress = extractReferralChallengeProgress(progressRes);
        if (!progress) {
          console.warn('[hub-referral-part]: referral challenge progress not found', progressRes);
          challengeCompletions.value = null;
          return;
        }
        cycleSize.value = progress.threshold;
        challengeCompletions.value = progress.completions;
      };

      const fetchReferralProgress = () => {
        let lifetime = 0;
        let pending = 0;

        return ShokzMember.getMemberDataByOnceCall()
          .then((memberData) => {
            lifetime = extractLifetimeReferrals(memberData);
            pending = extractPendingReferrals(memberData);
            const referralId = ShokzMember.id;
            if (!referralId) {
              console.warn('[hub-referral-part]: getReferralProgress skipped — ShokzMember.id is empty');
              return null;
            }
            return ShokzMember.getReferralProgress(referralId);
          })
          .then((progressRes) => {
            applyReferralProgressState(lifetime, pending, progressRes);
          })
          .catch((error) => {
            console.error('[hub-referral-part]: getReferralProgress error', error);
            applyReferralProgressState(lifetime, pending, null);
          });
      };

      const fetchAvailableRedemptions = () => {
        if (debugMode.value) {
          return Promise.resolve();
        }
        return ShokzMember.getMemberCoupons()
          .then((res) => {
            if (res?.code !== 0) {
              console.warn('[hub-referral-part]: getMemberCoupons failed', res);
              return;
            }
            const referralOnly = filterAvailableReferralRedemptionCoupons(res?.data);
            referralCoupons.value = referralOnly;
            availableRedemptions.value = referralOnly.length;
          })
          .catch((error) => {
            console.error('[hub-referral-part]: getMemberCoupons error', error);
          });
      };

      const applyDebugState = () => {
        debugMode.value = isReferralDebugEnabled();
        if (!debugMode.value) return;

        const debugCoupons = filterAvailableReferralRedemptionCoupons(
          REFERRAL_DEBUG_CONFIG.coupons
        );
        referralCoupons.value = debugCoupons;
        availableRedemptions.value = Math.max(
          0,
          Number(REFERRAL_DEBUG_CONFIG.available_redemptions) ||
            debugCoupons.length
        );
        applyPendingDebugOverride(pendingReferrals);
        console.info('[hub-referral-part]: debug mode enabled', REFERRAL_DEBUG_CONFIG);
      };

      const onRedeemReward = async (event) => {
        if (redeemLoading.value) return;

        const button = event?.currentTarget;
        if (!(button instanceof HTMLButtonElement)) return;

        if (!isRewardVariantAvailable(button)) {
          simpleToast?.show('This reward is currently out of stock.');
          return;
        }

        const variantId = button.dataset.variantId;
        if (!variantId) {
          simpleToast?.show('Please choose an available color before redeeming.');
          return;
        }

        if (isReferralRedeemGa4DebugEnabled()) {
          pushRedeemSelectContentEvent(button);
          console.info('[hub-referral-part]: GA4 debug select_content', window.dataLayer?.slice(-1));
          simpleToast?.show('GA4 debug: select_content pushed. Check dataLayer in console.');
          return;
        }

        const coupon = pickNearestExpiryCoupon(referralCoupons.value);
        if (!coupon) {
          simpleToast?.show('No available referral discount code found.');
          return;
        }

        pushRedeemSelectContentEvent(button);

        const spinner = button.querySelector('.loading__spinner');
        redeemLoading.value = true;
        syncAllRewardRedeemButtons(mountRoot, true);
        button.classList.add('loading');
        spinner?.classList.remove('hidden');

        try {
          await redeemReferralReward(variantId, coupon.code);
        } catch (error) {
          console.error('[hub-referral-part]: redeem failed', error);
          simpleToast?.show(
            error?.message || 'Failed to redeem reward. Please try again.'
          );
          button.classList.remove('loading');
          spinner?.classList.add('hidden');
          redeemLoading.value = false;
          syncAllRewardRedeemButtons(mountRoot, false);
        }
      };

      const onCopyReferralLink = async (trackCopyLinkGa4 = false) => {
        if (linkCopied.value || !referralLink.value) return;
        try {
          await navigator.clipboard.writeText(referralLink.value);
          if (trackCopyLinkGa4) {
            pushSelectContentEvent('Copy Referral Link');
          }
          linkCopied.value = true;
          simpleToast?.show(copyToastMessage.value, COPY_TOAST_DURATION);
          window.setTimeout(() => {
            linkCopied.value = false;
          }, COPY_TOAST_DURATION + COPY_TOAST_HIDE_TRANSITION);
        } catch (error) {
          console.error('[hub-referral-part]: copy failed', error);
          simpleToast?.show('Failed to copy referral link. Please try again.');
        }
      };

      const onClickTermsLink = () => {
        pushSelectContentEvent('View Details/Terms');
      };

      onMounted(() => {
        applyDebugState();
        syncAllRewardRedeemButtons(mountRoot, false);
        Promise.all([fetchReferralLink(), fetchReferralProgress()])
          .then(() => fetchAvailableRedemptions())
          .finally(() => {
            loading.value = false;
            applyRedeemGa4DebugOverrides(availableRedemptions, referralCoupons);
            syncAllRewardRedeemButtons(mountRoot, false);
          });
      });

      return {
        loading,
        redeemLoading,
        referralLink,
        referralLinkDisplay,
        linkCopied,
        cycleSteps,
        cycleTicks,
        cycleProgress,
        pendingBarCount,
        cycleSize,
        availableRedemptions,
        lifetimeReferrals,
        pendingReferrals,
        rewardsUnlocked,
        rewardsSubtitleHtml,
        lockedButtonLabel,
        onCopyReferralLink,
        onClickTermsLink,
        onRedeemReward,
      };
    },
  });

  app.mount(mountRoot);
  initRewardSwatchSelection(mountRoot);
  initRewardsCarousel(mountRoot);
  return app;
}

class HubReferralPart extends HTMLElement {
  /** @type {Record<string, unknown>} */
  data = undefined;

  constructor() {
    super();
    const scriptElement = this.querySelector('[data-json]');
    if (scriptElement) {
      try {
        this.data = JSON.parse(scriptElement.textContent || '{}');
      } catch (error) {
        console.error('[hub-referral-part]: invalid JSON config', error);
        this.data = {};
      }
    }
    createHubReferralPartVueEntry(this);
  }
}

if (!customElements.get('hub-referral-part')) {
  customElements.define('hub-referral-part', HubReferralPart);
}
